import {useState, useEffect, useRef, FC, HTMLProps, KeyboardEvent} from 'react';
import {Options, Sign, SpecialKey} from './types';
import {isSeparator} from './helpers';
import getOptions from './getOptions';
import markCursorPosition from './markCursorPosition';
import sanitize from './sanitize';
import parseNumeric from './parseNumeric';
import changeEventTarget from './changeEventTarget';
import softFormat from './softFormat';
import hardFormat from './hardFormat';
import subMarkWithCursor from './subMarkWithCursor';
import limitSelectionRange from './limitSelectionRange';
import round from './round';

export interface Props extends HTMLProps<HTMLInputElement> {
  options?: Options,
  signed?: boolean,
  value?: number,
}

const NumericInput: FC<Props> = ({signed = false, options, value, ...props}) => {
  const opt = getOptions(options);
  // <input type="text"> event does not support e.target.valueAsNumber
  // hence we keep valueAsNumber for use in onBlur-event hard format
  const [valueAsNumber, setValueAsNumber] = useState<number>(null);
  const [separatorPressed, setSeparatorPressed] = useState<boolean>(false);
  const [hasFocus, setFocus] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (value === null || value === undefined) {
      // reset value
      setValueAsNumber(null);
      inputRef.current.value = '';
    } else {
      const sanitizedValue = round(value, opt);
      if (!hasFocus && (Number.isFinite(value) || value === null) && sanitizedValue !== valueAsNumber && inputRef.current) {
        setValueAsNumber(sanitizedValue);
        inputRef.current.value = hardFormat(sanitizedValue, opt);
      }
    }
  }, [value]);

  return (
    <input {...props} type="text" ref={inputRef} name={props.name || props.id}
      onFocus={(e) => {
        setFocus(true);
        if (e.currentTarget.value === '') {
          e.currentTarget.value = opt.prefix + opt.suffix;
          e.target = changeEventTarget(e, valueAsNumber, opt);
          e.currentTarget.setSelectionRange(opt.prefix.length, opt.prefix.length);
        }
        if (props.onFocus) props.onFocus(e);
      }}
      onClick={(e) => {
        limitSelectionRange(e, opt);
        if (props.onClick) props.onClick(e);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        let {selectionStart: start, selectionEnd: end, value} = e.currentTarget;
        const isSelection = start !== end;
        const positionBeforeSuffix = value.length - opt.suffix.length;

        if (isSelection
          && ((start <= opt.prefix.length && end <= opt.prefix.length)
          || (start >= positionBeforeSuffix && end >= positionBeforeSuffix))) {
          // Selection with only the prefix or suffix selected.
          e.preventDefault();
          return;
        }
        if (e.key === SpecialKey.LEFT && start === opt.prefix.length) {
          e.currentTarget.setSelectionRange(opt.prefix.length, opt.prefix.length);
          e.preventDefault();
          return;
        }
        if (e.key === SpecialKey.RIGHT && end === positionBeforeSuffix) {
          e.currentTarget.setSelectionRange(positionBeforeSuffix, positionBeforeSuffix);
          e.preventDefault();
          return;
        }
        if (!isSelection && e.key === SpecialKey.BACKSPACE && start <= opt.prefix.length) {
          e.preventDefault();
          return;
        }
        if (!isSelection && e.key === SpecialKey.DELETE && end >= positionBeforeSuffix) {
          e.preventDefault();
          return;
        }

        // Prevent markup chars from being changed when selected
        let {selectionStart, selectionEnd} =  limitSelectionRange(e, opt).currentTarget;

        // Skip markups when no selection and remove-key pressed
        if (e.key === SpecialKey.BACKSPACE && selectionStart === selectionEnd
          && value.charAt(selectionStart - 1) === opt.groupSeparator) {
          // '2.|000' -> (backspace) -> '|.000'
          selectionStart -= 1;
          selectionEnd -= 1;
        }
        if (e.key === SpecialKey.DELETE && selectionStart === selectionEnd
          && value.charAt(selectionStart) === opt.groupSeparator) {
          // Skip markups: '2|.000' -> (delete) -> '2.|00'
          selectionStart += 1;
          selectionEnd += 1;
        }
        e.currentTarget.setSelectionRange(selectionStart, selectionEnd);

        if (isSeparator(e.key)) {
          setSeparatorPressed(true);
        }

        if (props.onKeyDown) props.onKeyDown(e);
      }}
      onChange={(e) => {
        // NOTE: options.radix and options.groupSeperator become same thing (one of is expected)
        const {selectionStart, selectionEnd, value} = e.currentTarget;

        // Check if number is signed
        const sign = signed && value.includes(Sign.MINUS) && !value.includes(Sign.PLUS);

        const marked = markCursorPosition(value, selectionStart, selectionEnd);
        let cleaned = sanitize(marked, sign, separatorPressed, opt);
        setSeparatorPressed(false);

        // Soft format string in input: € 0.000,00235| is allowed
        const formatted = softFormat(cleaned, sign, opt);
        subMarkWithCursor(e, formatted);

        const numeric = parseNumeric(cleaned, sign, opt);
        setValueAsNumber(numeric);

        e.target = changeEventTarget(e, numeric, opt);
        if (props.onChange) props.onChange(e);
      }}
      onBlur={(e) => {
        setFocus(false);
        // Hard format: € 0.00,00235 -> € 0
        const formatted = hardFormat(valueAsNumber, opt);
        e.currentTarget.value = formatted;
        e.target = changeEventTarget(e, valueAsNumber, opt);
        if (props.onBlur) props.onBlur(e);
      }}
    />
  );
}

export default NumericInput;
